<template>
  <div class="equipments-logs">
    <OmxList
        :data="equipmentsLogsData"
        :loading="isLoading"
        :show-search="false"
        empty-icon="robot-industrial"
    >
      <template #actions v-if="currentUserAccess('ProjectLogs', UserActions.CREATE, project_id)">
        <b-button type="is-primary" icon-left="plus-thick" @click="addNewEquipmentLogClicked">
          {{ $t('equipments-logs.new-equipment-log-button') }}
        </b-button>
      </template>

      <b-table-column field="date" :label="$t('equipments-logs.equipment-logdate-column')" v-slot="props">
        {{ props.row.date }}
      </b-table-column>
      <b-table-column field="model" :label="$t('equipments-logs.equipment-model-column')" v-slot="props">
        {{ props.row.model }}
      </b-table-column>
      <b-table-column field="provider" :label="$t('equipments-logs.equipment-provider-column')" v-slot="props">
        {{ props.row.provider }}
      </b-table-column>
      <b-table-column field="quantity" :label="$t('equipments-logs.equipment-quantity-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.equipmentLog.quantity, props.row.unit) }}
      </b-table-column>
      <b-table-column field="total" :label="$t('equipments-logs.equipment-total-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.total, props.row.unit) }}
      </b-table-column>
      <b-table-column field="rest" :label="$t('equipments-logs.equipment-rest-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.rest, props.row.unit) }}
      </b-table-column>
      <b-table-column field="text" :label="$t('equipments-logs.equipment-text-column')" v-slot="props">
        {{ props.row.equipmentLog.text }}
      </b-table-column>
      <b-table-column
          v-if="currentUserAccess('ProjectLogs', UserActions.DELETE, project_id)"
          :label="$t('equipments-logs.actions-column')" centered v-slot="props" width="10%">
        <b-tooltip :label="$t('common.edit')" position="is-top">
          <b-button type="is-primary" size="is-small" class="m-1" icon-left="pencil"
                    @click="editEquipmentLogClicked(props.row.equipmentLog)" />
        </b-tooltip>
        <b-tooltip :label="$t('common.delete')" position="is-top">
          <b-button type="is-danger" size="is-small" class="m-1" icon-left="delete"
                    @click="deleteEquipmentLogClicked(props.row.equipmentLog)" />
        </b-tooltip>
      </b-table-column>
    </OmxList>

    <b-modal
        v-model="isEquipmentLogModalActive"
        trap-focus
        :destroy-on-hide="true"
        aria-role="dialog"
        aria-modal>
      <template #default="props">
        <EquipmentLog :project_id="project_id" :equipments_data="equipmentsData" :log_data="selected_log" :selected_date="selected_date"
                      @close="props.close"
                      @equipmentLogAdded="readEquipmentsLogsData" @equipmentLogUpdated="readEquipmentsLogsData" />
      </template>
    </b-modal>
  </div>
</template>

<script>

import firebase from "firebase/app";
import EquipmentLog from "@/components/EquipmentLog";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "EquipmentsLogs",
  components: {
    EquipmentLog,
    OmxList
  },
  props: {
    'project_id': {
      type: String,
    },
    'selected_date': {}
  },
  watch: {
    selected_date: function () {
      this.dateSelectClicked(this.selected_date)
    }
  },
  data() {
    return {
      modelDate: new Date(),
      selectedUntilDate: new Date((new Date()).setHours(23, 59, 59, 0)),
      selectedFromDate: new Date((new Date()).setHours(0, 0, 0, 0)),
      equipmentsData: [],
      equipmentsLogsData: [],
      isLoading: false,
      isEquipmentLogModalActive: false,
      selected_log: {}
    }
  },
  methods: {
    readEquipmentsData: function () {
      this.equipmentsData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getEquipmentsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getEquipmentsFunction({action: "get_equipments", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            if (result.data.equipments.length > 0) {
              result.data["equipments"].forEach((equipment) => {
                this.equipmentsData.push({
                  equipment: equipment,
                  model: equipment.model
                });
              });
            }
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting equipments:", error?.code);
          });
    },
    readEquipmentsLogsData: function () {
      this.isEquipmentLogModalActive = false;
      this.isLoading = true;
      this.equipmentsLogsData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getEquipmentsLogsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payloadData = {
        action: "get_logs",
        project_id: this.project_id,
        equipment_id: "*",
        fill_meta: true,
        filters: [['date', '>', this.selectedFromDate.toISOString()], ['date', '<', this.selectedUntilDate.toISOString()]]
      }
      getEquipmentsLogsFunction(payloadData)
          .then((result) => {
            if (result.data.equipmentslogs.length > 0) {
              result.data["equipmentslogs"].forEach((equipmentLog) => {
                this.equipmentsLogsData.push({
                  equipmentLog: equipmentLog,
                  total: equipmentLog.parent.logged_quantities,
                  rest: parseFloat(equipmentLog.meta.estimation.quantity) - parseFloat(equipmentLog.parent.logged_quantities),
                  unit: equipmentLog.parent.unit,
                  model: equipmentLog.parent.model,
                  provider: equipmentLog.parent.provider,
                  date: this.getFormattedDate(equipmentLog.date),
                  document_id: equipmentLog.document_id
                });
              });
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message)
            console.log("Error getting equipment logs:", error?.code);
          });
    },
    deleteEquipmentLog: function (equipmentLog) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteEquipmentLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteEquipmentLogFunction({
        action: "delete_log",
        project_id: equipmentLog.project_id,
        equipment_id: equipmentLog.equipment_id,
        document_id: equipmentLog.document_id,
      }).then(() => {
        this.showSuccess(this.$t('messages.equipment-log-deleted'));
        this.readEquipmentsLogsData()
      }).catch((error) => {
        this.showError(this.$t('messages.save-error'))
        console.error("Error deleting log:", error?.code);
      });
      this.isLoading = false;
    },
    /*
     UI Events
     */
    addNewEquipmentLogClicked: function () {
      this.readEquipmentsData();
      this.selected_log = {};
      this.selected_log.document_id = "new";
      this.isEquipmentLogModalActive = true;
    },
    editEquipmentLogClicked: function (equipmentLog) {
      this.readEquipmentsData();
      this.selected_log = equipmentLog;
      this.isEquipmentLogModalActive = true;
    },
    dateSelectClicked: function (date) {
      const fromDate = date.setHours(0, 0, 0, 0)
      const untilDate = date.setHours(23, 59, 59, 0)
      this.selectedFromDate = new Date(fromDate)
      this.selectedUntilDate = new Date(untilDate)
      this.readEquipmentsLogsData();
    },
    async deleteEquipmentLogClicked(equipmentLog) {
      const ok = await this.$omxConfirm({
        title: this.$t('equipments-logs.delete-log-title').toString(),
        message: this.$t('equipments-logs.delete-log-message').toString(),
        confirmText: this.$t('equipments-logs.delete-log-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (!ok) return;
      this.deleteEquipmentLog(equipmentLog);
      this.readEquipmentsData();
    }
  },
  mounted() {
    this.readEquipmentsLogsData();
  }
};
</script>

<style scoped>
.equipments-logs {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
